import { Component, OnInit, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import {
  FormGroup,
  FormArray,
  FormBuilder,
  AsyncValidatorFn,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Store, ofActionSuccessful, Actions, select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { Subscription, SubscriptionStatusFrozenSuspendedText } from '@shared/models/subscription-model';
import { ApiGetOfferingsAction } from '@sales/states/offering/offering.actions';
import { VoiceProduct } from '@shared/models/voice-product.model';
import { OrderBuilderStepComponent } from './subscription-stepper/order-builder-step/order-builder-step.component';
import { NumberChooserStepComponent } from './subscription-stepper/number-chooser-step/number-chooser-step.component';
import { UserDefinerStepComponent } from './subscription-stepper/user-definer-step/user-definer-step.component';
import {
  ProductAddAction,
  ToggleSelectedSubscriptionAction,
  SetSelectedStepIndexAction,
  ApiGetPortingDatesAction,
} from '@sales/states/customer-product/customer-product.actions';
import { AdditionalServicesStepComponent } from './subscription-stepper/additional-services-step/additional-services-step.component';
import { AppData } from '@shared/models/appdata.model';
import { ProductClassifications } from '@core/enums/product-classifications';
import { OpenDialogService } from '@core/services/open-dialog.service';
import { CustomerProductSelectors } from '@sales/states/customer-product/customer-product.selectors';
import { MessageType } from '@shared/enums/message-type.enum';
import { IconNames } from '@shared/enums/icon-names';
import { SimService } from '@sales/services/sim.service';
import { SimCardValidator } from '@sales/validators/async-sim-card-validator';
import { SimCardType } from '@sales/models/simcard.model';
import { FormStatus } from '@sales/models/form-status.enum';
import { DealerStateSelectors } from '@core/states/dealer/dealer-state.selectors';
import { CustomerSelectors } from '../../details/states/customer.selectors';
import { InFlightOrderItemTypes } from '@core/models/in-flight-order.model';
import { SubscriptionService } from '@sales/services/subscription.service';
import { SubscriptionHelperService } from '@app/sales/services/subscription-helper.service';
import { CustomerService } from '../../details/services/customer.service';
import { take, tap } from 'rxjs/operators';
import { ApiGetExistingSubscriptionsSuccessAction } from '../../details/states/customer.actions';
import { OfferingHelperService } from '@app/sales/services/offering-helper.service';
import { FormStateModel, FormWithStepper } from '@app/sales/states/form/form.types';
import { Regex } from '@app/shared/enums/regex';
import { FormSelectors } from '@app/sales/states/form/form.selectors';
import { SortByPipe } from '@sales/modules/mbb/pipes/sort-by.pipe';
import { AsyncPipe, DatePipe } from '@angular/common';
import { ExistingSubscriptionComponent } from './existing-subscription/existing-subscription.component';
import { IceAlertComponent } from '@app/shared/components/molecules/ice-alert/ice-alert.component';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';
import { IceBadgeComponent } from '@app/shared/components/atoms/ice-badge/ice-badge.component';
import { CustomerAgreementComponent } from '@app/shared/components/customer-agreement/customer-agreement.component';
import { SubscriptionStepperComponent } from './subscription-stepper/subscription-stepper.component';
import { AccordionLazyDirective } from '@app/shared/directives/accordion-lazy.directive';
import { AccordionContentComponent } from '@app/shared/components/molecules/accordion/accordion-content/accordion-content.component';
import { MatMenuItem } from '@angular/material/menu';
import { AccordionHeaderComponent } from '@app/shared/components/molecules/accordion/accordion-header/accordion-header.component';
import { AccordionGroupComponent } from '@app/shared/components/molecules/accordion/accordion-group/accordion-group.component';
import { AccordionComponent } from '@app/shared/components/molecules/accordion/accordion.component';
import { NgxsFormDirective } from '@ngxs/form-plugin';
import { IceCardComponent } from '@app/shared/components/atoms/ice-card/ice-card.component';
import { AddProductButtonComponent } from '@app/shared/components/atoms/add-product-button/add-product-button.component';
import { hasVoiceSubscriptionChanged } from '@app/core/utils';

@Component({
  selector: 'app-subscription',
  templateUrl: './subscription.component.html',
  styleUrls: ['./subscription.component.scss'],
  imports: [
    AddProductButtonComponent,
    IceCardComponent,
    FormsModule,
    NgxsFormDirective,
    ReactiveFormsModule,
    AccordionComponent,
    AccordionGroupComponent,
    AccordionHeaderComponent,
    MatMenuItem,
    AccordionContentComponent,
    AccordionLazyDirective,
    SubscriptionStepperComponent,
    CustomerAgreementComponent,
    IceBadgeComponent,
    SpinnerComponent,
    IceAlertComponent,
    ExistingSubscriptionComponent,
    AsyncPipe,
    DatePipe,
    SortByPipe,
  ],
})
export class SubscriptionComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private formBuilder = inject(FormBuilder);
  private cd = inject(ChangeDetectorRef);
  private simService = inject(SimService);
  private subscriptionService = inject(SubscriptionService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private appData = inject(AppData);
  private openDialogService = inject(OpenDialogService);
  private customerService = inject(CustomerService);
  private actions$ = inject(Actions);

  MessageType = MessageType;
  FormStatus = FormStatus;
  IconNames = IconNames;
  SubscriptionHelperService = SubscriptionHelperService;
  SubscriptionStatusFrozenSuspendedText = SubscriptionStatusFrozenSuspendedText;
  hasVoiceSubscriptionChanged = hasVoiceSubscriptionChanged;

  products$: Observable<VoiceProduct[]> = this.store.select<VoiceProduct[]>(({ customerProducts }) =>
    customerProducts ? customerProducts.products : []
  );

  formState = select(FormSelectors.getState);
  isLoadingProducts = select(CustomerSelectors.isLoadingProducts);
  numberOfSubscriptionAllowed = select(CustomerSelectors.numberOfSubscriptionAllowed);
  allowanceCheckSuccessful = select(CustomerSelectors.allowanceCheckSuccessful);
  hasPerformedAllowanceCheck = select(CustomerSelectors.hasPerformedAllowanceCheck);
  isLoadingExistingProductDetails = select(CustomerProductSelectors.isLoadingExistingProductDetails);
  selectedSubscriptionId = select(CustomerProductSelectors.selectedSubscriptionId);
  selectedStep = select(CustomerProductSelectors.selectedStep);
  canSellMobilePhone = select(DealerStateSelectors.canSellMobilePhone);
  isEquipmentUpgradeEnabled = select(DealerStateSelectors.isEquipmentUpgradeEnabled);
  inFlightVoiceOrders = select(CustomerProductSelectors.inFlightVoiceOrdersUnprocessed);
  inFlightServicesOrders = select(CustomerSelectors.inFlightServicesOrders);

  products: VoiceProduct[] = [];
  existingProducts: VoiceProduct[] = [];
  InFlightOrderItemTypes = InFlightOrderItemTypes;
  VoiceProduct = VoiceProduct;

  form: FormGroup = new FormGroup({ list: new FormArray([]) });

  subscriptions$ = [];

  get list(): FormGroup[] {
    return (this.form.get('list') as FormArray).controls as FormGroup[];
  }

  public static buildFormFromExistingState(
    products: VoiceProduct[],
    formState: FormStateModel,
    formBuilder: FormBuilder,
    store: Store,
    iccValidator: AsyncValidatorFn,
    subscriptionService: SubscriptionService,
    customerService: CustomerService
  ): FormGroup {
    const items = formState.subscriptionForm.model.list.map((item: FormWithStepper) => {
      const additionalServicesArray = new FormArray([]);
      if (item.additionalServices) {
        item.additionalServices.forEach(service => {
          if (service.isMutexService) {
            const group = AdditionalServicesStepComponent.buildMutexServiceFormGroup(
              service.mutexName,
              service.selected,
              service.selectedMutexService
            );
            additionalServicesArray.push(group);
          } else {
            if (service.requireICC) {
              const group = AdditionalServicesStepComponent.buildICCFormGroup(
                service.productId,
                service.selected,
                service.icc,
                service.simDelivery,
                iccValidator
              );
              additionalServicesArray.push(group);
            } else {
              const group = AdditionalServicesStepComponent.buildFormGroup(service.productId, service.selected, false);
              additionalServicesArray.push(group);
            }
          }
        });
      }
      const currentVoiceProduct: VoiceProduct = products.find(p => p.offering.id === item.chooseSubscription?.offering);

      return formBuilder.group({
        productId: item.productId,
        interactedSteps: formBuilder.control(item.interactedSteps),
        chooseSubscription: OrderBuilderStepComponent.buildChooseSubscriptionFormGroup(item.chooseSubscription?.offering),
        numberChooser: NumberChooserStepComponent.buildNumberChooserFormGroup(
          item.productId,
          item.numberChooser?.icc,
          item.numberChooser?.orderSim,
          item.numberChooser?.isESim,
          item.numberChooser?.isPortingOrder,
          item.numberChooser?.isOwnerChange,
          item.numberChooser?.keepExistingSim,
          item.numberChooser?.existingOwnerCustomerId,
          item.numberChooser?.aNumber,
          item.numberChooser?.activationDateTime,
          item.numberChooser?.portingTime,
          item.numberChooser?.isEarliestPortingDate,
          store,
          iccValidator,
          subscriptionService,
          currentVoiceProduct,
          customerService
        ),
        user: UserDefinerStepComponent.buildUserDefinerFormGroup(
          item.user?.name,
          item.user?.birthday,
          item.user?.address,
          item.user?.postCode,
          item.user?.city,
          item.user?.email,
          item.user?.dontHaveEmail,
          item.user?.phoneBookVisibility,
          currentVoiceProduct?.offering?.upperAgeLimit,
          currentVoiceProduct?.offering?.lowerAgeLimit
        ),
        additionalServices: additionalServicesArray,
        whitelistEntries: formBuilder.group({
          whitelistEntry1: formBuilder.control(item.whitelistEntries ? item.whitelistEntries[0] : '', [
            Validators.pattern(Regex.Phone),
          ]),
          whitelistEntry2: formBuilder.control(item.whitelistEntries ? item.whitelistEntries[1] : '', [
            Validators.pattern(Regex.Phone),
          ]),
          whitelistEntry3: formBuilder.control(item.whitelistEntries ? item.whitelistEntries[2] : '', [
            Validators.pattern(Regex.Phone),
          ]),
        }),
      });
    });
    return formBuilder.group({
      list: formBuilder.array(items),
    });
  }

  static getNewVoiceProductFormState(product: VoiceProduct) {
    return {
      productId: product.id,
      interactedSteps: [0],
      chooseSubscription: OrderBuilderStepComponent.getChooseSubscriptionFormStateValue(product),
    };
  }

  ngOnInit(): void {
    const id = this.selectedSubscriptionId()?.toString();
    if (id && this.route.snapshot.queryParamMap.get('id') !== id) {
      this.router.navigate([], { relativeTo: this.route, queryParams: { id } });
    }

    this.store.dispatch([new ApiGetOfferingsAction(), new ApiGetPortingDatesAction()]);

    this.subscriptions$.push(this.openSelectedSubscription(this.route.snapshot.queryParamMap.get('id')));

    this.subscriptions$.push(
      this.products$.subscribe({
        next: (products: VoiceProduct[]) => {
          this.products = products.filter(p => !p.isExistingSubscription);
          this.existingProducts = products.filter(p => p.isExistingSubscription);

          this.updateFormFromProducts();

          this.cd.markForCheck();
        },
      })
    );

    this.form = SubscriptionComponent.buildFormFromExistingState(
      this.products,
      this.formState(),
      this.formBuilder,
      this.store,
      SimCardValidator(this.simService, SimCardType.Voice),
      this.subscriptionService,
      this.customerService
    );
  }

  private updateFormFromProducts() {
    const existingProductsInForm = (this.form.get('list') as FormArray).controls;
    // Remove from form if some product is deleted
    existingProductsInForm.forEach(existing => {
      const existingId = existing.get('productId').value;
      if (!this.products.find(p => p.id === existingId)) {
        const indexToRemove = existingProductsInForm.indexOf(existing);
        (this.form.get('list') as FormArray).removeAt(indexToRemove);
      }
    });
    // Add new product to form
    this.products.forEach(product => {
      const existing = existingProductsInForm.find(c => c.get('productId').value === product.id);
      if (!existing) {
        (this.form.get('list') as FormArray).push(this.createSubscriptionItemFormGroup(product));
      }
    });
  }

  private createSubscriptionItemFormGroup(item: VoiceProduct) {
    return this.formBuilder.group({ productId: item.id, interactedSteps: this.formBuilder.control([]) });
  }

  onAddVoiceProduct() {
    const isCustomerAllowedToAddAdditionalSubscriptions = this.store.selectSnapshot<boolean>(
      CustomerSelectors.isCustomerAllowedToAddAdditionalSubscriptions
    );

    if (this.numberOfSubscriptionAllowed() > 0) {
      const isCurrentSubscriptionAmountReached =
        this.products.length + this.existingProducts.length >= this.numberOfSubscriptionAllowed();
      if (isCurrentSubscriptionAmountReached || isCustomerAllowedToAddAdditionalSubscriptions) {
        this.openDialogService.openMaximumNumberOfSubscriptionsDialog();
        return;
      }
    }

    this.store.dispatch(new ProductAddAction());
  }

  onToggleAccordion(product: VoiceProduct, item?: FormGroup) {
    if (!product) return;

    if (!product.isExistingSubscription) {
      if (item) {
        this.setInteractedStep(item);
      }
      this.store.dispatch([
        new SetSelectedStepIndexAction(product.offering?.id ? 1 : 0),
        new ToggleSelectedSubscriptionAction(product.id),
      ]);
    } else {
      this.store.dispatch(new ToggleSelectedSubscriptionAction(product.id));
    }
  }

  onRemoveVoiceProduct(product: VoiceProduct) {
    this.openDialogService.openRemoveProductDialog(product, ProductClassifications.VoiceProduct);
  }

  onFinishSubscription(product: VoiceProduct, item: FormGroup) {
    this.setInteractedStep(item);
    this.store.dispatch(new SetSelectedStepIndexAction(product.offering?.id ? 1 : 0));
    this.store.dispatch(new ToggleSelectedSubscriptionAction(product.id));
  }

  private setInteractedStep(item: FormGroup) {
    const interactedSteps = item.controls.interactedSteps.value;
    if (!interactedSteps.includes(this.selectedStep())) {
      item.controls.interactedSteps.setValue([...interactedSteps, this.selectedStep()]);
    }
  }

  openSelectedSubscription(subscriptionId: string) {
    return this.actions$
      .pipe(
        ofActionSuccessful(ApiGetExistingSubscriptionsSuccessAction),
        take(1), // We're only interested in the first emission
        tap(({ subscriptions }) => {
          const customerProduct: Subscription = subscriptions.find(
            (subscription: Subscription) => subscription.id.toString() === subscriptionId
          );
          if (customerProduct) {
            return this.store.dispatch(new ToggleSelectedSubscriptionAction(customerProduct.id));
          }
        })
      )
      .subscribe();
  }

  toManny(product: VoiceProduct) {
    const url = `${this.appData.mannyBaseUrl}/customer/${product.subscription?.customerId}/subscription/${product.subscription?.id}`;
    window.open(url, '_blank');
  }

  getSubscriptionName(product: VoiceProduct): string {
    if (!product) return '';

    const vasProduct = OfferingHelperService.getVasProductFromOffering(product.offering);
    const mobilePlanProductName = OfferingHelperService.getMobilePlanProductFromOffering(product.offering)?.productName;

    if (!!vasProduct) return mobilePlanProductName;
    if (product.isExistingSubscription) return product.offering?.name ?? product.assignedSubscription?.displayName;
    return product.subscription?.productName ?? product.offering?.name ?? 'Nytt abonnement';
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
